import BarChart from './charts/BarChart';
import PieChart from './charts/PieChart';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import styles from "./analytics.module.scss";
import { Info, Table, ChartPie, FileDown } from "lucide-react";
import { useState, useMemo, useRef } from "react";

const CHART_COMPONENTS = {
    // Clé : Nom du composant React
    'barChart': BarChart,
    'pieChart': PieChart,
    "doughnut": PieChart,
    "polarArea": PieChart,

};

// Transforme le format { labels, datasets } en lignes + colonnes
function buildTable(data) {
    const labels = data?.labels ?? [];
    const datasets = data?.datasets ?? [];

    // plusieurs séries (graphique temporel type barChart)
    if (datasets.length > 1) {
        const columns = [
            { field: 'period', header: 'Période', type: 'text' },
            ...datasets.map((ds, index) => ({
                field: `series_${index}`,
                header: ds.label || `Accueil`,
                type: 'number',
            })),
        ];

        const rows = labels.map((label, rowIndex) => {
            const row = {
                __key: `${label}-${rowIndex}`,
                period: label,
            };

            datasets.forEach((ds, index) => {
                row[`series_${index}`] = ds.data?.[rowIndex] ?? 0;
            });

            return row;
        });

        return { rows, columns };
    }

    // série unique (pie, doughnut, polarArea)
    const dataset = datasets[0] ?? {};
    const values = dataset.data ?? [];
    const total = values.reduce((sum, value) => sum + (value || 0), 0);

    const rows = labels.map((label, index) => ({
        __key: `${label}-${index}`,
        label: label || '—',
        value: values[index] ?? 0,
        share: total > 0 ? (values[index] ?? 0) / total : 0,
    }));

    return {
        rows,
        columns: [
            { field: 'label', header: 'Label', type: 'text' },
            { field: 'value', header: dataset.label || 'Valeur', type: 'number' },
            { field: 'share', header: '%', type: 'percent' },
        ],
    };
}

function ChartContainer({ label, data, info, config, type, isLoading }) {
    const ChartComponent = CHART_COMPONENTS[type];
    const datatableRef = useRef(null);

    const [displayMode, setDisplayMode] = useState("graph");
    const table = useMemo(
        () => buildTable(data),
        [data]
    );

    const handleDownloadCsvExport = () => {
        datatableRef.current.exportCSV();
    };


    if (isLoading) {
        return (
            <div className={styles.chartContainer}>
                <div className={styles.top}>
                    <p className={styles.label}>{label}</p>
                </div>
                <div className={styles.loadingContainer}>
                    <div className="w-8 h-8 border-4 border-slate-300 border-t-blue-500 rounded-full animate-spin"></div>
                </div>
            </div>
        );
    }
    if (!data || !data.labels || !data.datasets?.length || !data.labels.length) {
        return (
            <div className={styles.chartContainer}>
                <div className={styles.top}>
                    <p className={styles.label}>{label}</p>
                </div>
                <div className={styles.emptyContainer}>
                    <div className={styles.empty}>
                        Aucune donnée disponible pour ce graphique.
                    </div>
                </div>
            </div>
        );
    }

    if (!ChartComponent) {
        console.warn(`Le type de graphique "${type}" n'est pas configuré.`);
        return (
            <div className={styles.chartContainer}>
                <div className={styles.top}>
                    <p className={styles.label}>{label}</p>
                </div>
                <div className={styles.loadingContainer}>
                    <div className={styles.error}>
                        Configuration manquante pour ce graphique.
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.chartContainer}>
            <div className={styles.top}>
                <p className={styles.label}>{label}</p>

                <div className={styles.infoContainer}>
                    {info && <Info className={styles.icon} />}
                    <p className={styles.tooltip}>{info}</p>
                    {displayMode === "graph" ?
                        <Table onClick={() => setDisplayMode("table")} className={`${styles.icon} ${styles.iconClickable}`} />
                        : <>
                            <FileDown onClick={() => handleDownloadCsvExport()} className={`${styles.icon} ${styles.iconClickable}`} />
                            <ChartPie onClick={() => setDisplayMode("graph")} className={`${styles.icon} ${styles.iconClickable}`} />
                        </>
                    }

                </div>
            </div>
            {displayMode === "graph" ?

                <ChartComponent data={data} config={config} />
                :
                <DataTable
                    ref={datatableRef}
                    value={table.rows}
                    dataKey="__key"
                    size="small"
                    stripedRows
                    showGridlines
                    scrollable
                    resizableColumns
                    columnResizeMode="fit"
                    onColumnResizeEnd={(e) => localStorage.setItem('nwm-analytics-table-widths', JSON.stringify(e.columnWidths))}
                    scrollHeight="1200px"
                    paginator={table.rows.length > 15}
                    rows={15}
                    className={styles.table}
                >
                    {table.columns.map((column) => (
                        <Column
                            key={column.field}
                            field={column.field}
                            header={column.header}
                            sortable={column.type === 'percent'}
                            dataType="number"
                            // style={{ minWidth: '100px', width: '100px' }}
                            body={(row) => {
                                const value = row[column.field];

                                if (column.type === 'percent') {
                                    return `${(value * 100).toFixed(1)} %`;
                                }

                                if (column.type === 'number') {
                                    return value.toLocaleString('fr-FR');
                                }

                                return value;
                            }}
                        />
                    ))}
                </DataTable>

            }
        </div>
    );
}

export default ChartContainer;

